/**
 * Retarificación — when a tarifario changes (tramos edited, or a more specific
 * table now out-matches the general one), find the fianzas whose PENDING
 * comisión was priced off the replaced pricing and compute what each would
 * earn under the new table, so the owner can confirm which ones to update.
 *
 * Only pendiente movements qualify: a cobrada comisión is money received at
 * the old %, a manual override restates a negotiated fact, and reversals
 * derive from what was actually booked — none of those move on a table edit.
 */
import {
  getPrimaAmount,
  calculateTarifa,
  calculateComision,
  toTimestamp,
  partitionTarifas,
  getEmisorNombre,
  getEmisorRfc,
  getSubRamo,
  getTipoFianza,
  TARIFA_AMBITO_CONVENIO,
} from './calculations';

const norm = (s) => String(s || '').toLowerCase();

const elegible = (m) =>
  m?.estadoCobro === 'pendiente' && !m?.manualOverride && m?.prima != null;

/**
 * Movements a change to `tarifa` can re-price.
 *
 * esNueva=false (tramos edited): movements priced by this exact table.
 * esNueva=true (table just created): movements of this emisor matching the
 * table's subRamo/tipo that were priced by ANOTHER table (or none) — the new
 * table now out-matches whatever priced them.
 *
 * Propia tables never touch convenio-side movements and vice versa.
 *
 * `tarifas` (all of the org's tables) lets a new convenio Tabla general also
 * claim the convenio movements that fell through to a propia table.
 */
export function findRetarifaCandidates(movements, tarifa, { esNueva = false, tarifas = null } = {}) {
  const esConvenioTabla = tarifa?.ambito === TARIFA_AMBITO_CONVENIO;
  const convenioIds = tarifas
    ? new Set(partitionTarifas(tarifas).convenio.map((t) => t.id))
    : null;
  return (movements || []).filter((m) => {
    if (!elegible(m)) return false;
    if (esConvenioTabla ? m.splitRol !== 'convenio' : m.splitRol === 'convenio') return false;
    if (!esNueva) return m.tarifaId === tarifa.id;
    // A just-created Tabla general can price everything nothing priced before.
    // A convenio one additionally out-matches the propia tables that were
    // pricing convenio movements as the implicit default.
    if (tarifa.esGeneral) {
      if (m.tarifaId == null) return true;
      return Boolean(esConvenioTabla && convenioIds && !convenioIds.has(m.tarifaId));
    }
    // RFC-first emisor match (movements carry afianzadoraRfc in their facets);
    // exact name covers movements/tables that predate the field. A new
    // specific table also out-matches the Tabla general (tarifaId differs).
    const sameEmisor =
      (m.afianzadoraRfc && tarifa.emisorRfc && m.afianzadoraRfc === tarifa.emisorRfc) ||
      m.afianzadora === tarifa.emisor;
    return (
      sameEmisor &&
      (!tarifa.subRamo || norm(m.subRamo) === norm(tarifa.subRamo)) &&
      (!tarifa.tipo || norm(m.tipoFianza) === norm(tarifa.tipo)) &&
      m.tarifaId !== tarifa.id
    );
  });
}

/**
 * Per-fianza retarifa plan: group candidates, resolve each fianza's NEW % from
 * the updated tables (each fianza's tarifa metric picks its own tramo), and
 * diff against what is booked. Fianzas whose % does not change are dropped.
 *
 * @returns [{fianzaId, contratoId, numeroFianza, nombre, pctAnterior,
 *            pctNuevo, montoAnterior, montoNuevo, movimientos}]
 */
export function buildRetarifaPlan(candidates, contractsList, tarifas, tarifa) {
  const byFianza = new Map();
  (candidates || []).forEach((m) => {
    if (!m?.fianzaId) return;
    if (!byFianza.has(m.fianzaId)) byFianza.set(m.fianzaId, []);
    byFianza.get(m.fianzaId).push(m);
  });

  const contractsById = new Map((contractsList || []).map((c) => [c.id, c]));
  const { propias, convenio } = partitionTarifas(tarifas);
  const tablas = tarifa?.ambito === TARIFA_AMBITO_CONVENIO ? convenio : propias;

  const plan = [];
  byFianza.forEach((movs, fianzaId) => {
    const contract = contractsById.get(movs[0].contratoId);
    const fianza = (contract?.fianzas || []).find((f) => f.id === fianzaId);
    if (!fianza) return;
    // A % a person confirmed is not the table's to change (the backend skips
    // these too) — do not even offer them.
    if (fianza.comisionPctConfirmada) return;

    const prima = getPrimaAmount(fianza);
    const tarifaValor = calculateTarifa(prima, Number(fianza.montoAfianzado) || 0, {
      truncate: true,
      vigencia: {
        inicio: toTimestamp(fianza.inicioVigenciaFianza),
        fin: toTimestamp(fianza.finVigenciaFianza),
      },
    });
    const { comisionPct: pctNuevo } = calculateComision(
      prima, tarifaValor, tablas,
      getEmisorNombre(fianza), getSubRamo(fianza), getTipoFianza(fianza),
      getEmisorRfc(fianza)
    );
    if (pctNuevo == null) return;

    const pctAnterior = movs.find((m) => m.comisionPct != null)?.comisionPct ?? null;
    if (pctAnterior === pctNuevo) return;

    const factor = (m) => (m.splitRol ? (Number(m.splitPct) || 0) / 100 : 1);
    const montoAnterior = movs.reduce((s, m) => s + (Number(m.comisionMonto) || 0), 0);
    const montoNuevo = movs.reduce(
      (s, m) => s + ((Number(m.prima) || 0) * pctNuevo / 100) * factor(m), 0);

    plan.push({
      fianzaId,
      contratoId: movs[0].contratoId,
      numeroFianza: movs[0].numeroFianza || null,
      nombre: movs[0].clienteNombre || movs[0].convenioNombre || null,
      pctAnterior,
      pctNuevo,
      montoAnterior,
      montoNuevo,
      movimientos: movs.length,
    });
  });

  return plan.sort((a, b) => (b.montoNuevo - b.montoAnterior) - (a.montoNuevo - a.montoAnterior));
}
